import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faPen,
  faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactCheckbox from 'shared/design-system/components/TrueImpactCheckbox';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { AdminProgramQuestion, AdminProgramQuestionsSection } from './types';
import { feedbackSectionIdToString } from 'npo/pages/NPOFeedback/utils';

interface SectionAccordionProps {
  section: AdminProgramQuestionsSection;
  /** Locally-flagged state (with any pending, unsaved toggles applied). */
  flagState: Record<number, boolean>;
  disabled: boolean;
  /** Toggle the local flag. The parent decides when to persist. */
  onToggleFlag: (question: AdminProgramQuestion, next: boolean) => unknown;
  onEditQuestion: (question: AdminProgramQuestion) => unknown;
}

const Card = styled.div`
  background: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.greyLight};
  border-radius: 12px;
  margin-bottom: ${SPACERS.small};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  padding: ${SPACERS.small} ${SPACERS.medium};
  cursor: pointer;
  user-select: none;
  .section-title {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    .name {
      font-weight: 600;
      color: ${COLORS.blackBase};
    }
    .meta {
      color: ${COLORS.grayDark};
      font-size: 13px;
    }
  }
  .flagged-pill {
    padding: 4px 10px;
    border-radius: 12px;
    background: ${COLORS.ctaBrandBackground};
    color: ${COLORS.ctaBrand};
    font-size: 12px;
    font-weight: 700;
  }
`;

const Body = styled.div`
  border-top: 1px solid ${COLORS.greyLight};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr auto auto;
  align-items: center;
  gap: ${SPACERS.small};
  padding: ${SPACERS.small} ${SPACERS.medium};
  border-bottom: 1px solid ${COLORS.greyLightest};
  &:last-child {
    border-bottom: none;
  }
  .q-text {
    color: ${COLORS.blackBase};
  }
  .q-meta {
    color: ${COLORS.grayDark};
    font-size: 12px;
  }
  .orphan-warning {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px;
    border-radius: 10px;
    background: ${COLORS.warningBackground};
    color: ${COLORS.warningDark};
    font-size: 12px;
  }
`;

const SectionAccordion: React.FC<SectionAccordionProps> = ({
  section,
  flagState,
  disabled,
  onToggleFlag,
  onEditQuestion,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const label = section.section
    ? feedbackSectionIdToString(section.section)
    : 'Uncategorized';
  return (
    <Card>
      <Header onClick={() => setExpanded(x => !x)}>
        <div className="section-title">
          <span className="name">{label}</span>
          <span className="meta">
            {section.total} question{section.total === 1 ? '' : 's'} ·{' '}
            {section.programFlagged} flagged as program-level
          </span>
        </div>
        {section.programFlagged > 0 && (
          <span className="flagged-pill">
            {section.programFlagged}/{section.total}
          </span>
        )}
        <TrueImpactIconButton
          icon={expanded ? faChevronUp : faChevronDown}
          label={expanded ? 'Collapse' : 'Expand'}
          onClick={() => setExpanded(x => !x)}
        />
      </Header>
      <TrueImpactCollapse show={expanded}>
        <Body>
          {section.questions.map(q => {
            const currentFlag = flagState[q.questionId] ?? q.isProgramQuestion;
            const wouldOrphan =
              !currentFlag && q.isProgramQuestion && q.programAnswersCount > 0;
            return (
              <Row key={q.questionId}>
                <TrueImpactCheckbox
                  checked={currentFlag}
                  text=""
                  handleCheck={next => onToggleFlag(q, next)}
                  disabled={disabled}
                />
                <div>
                  <TrueImpactText variant="body" className="q-text">
                    {q.text || q.name || `Question #${q.questionId}`}
                  </TrueImpactText>
                  <TrueImpactText variant="body-small" className="q-meta">
                    #{q.questionId} · {q.questionType || 'unknown'} ·{' '}
                    {q.reportingType || 'full'}
                    {q.required ? ' · required' : ''}
                    {q.programAnswersCount > 0
                      ? ` · ${q.programAnswersCount} program answer${
                          q.programAnswersCount === 1 ? '' : 's'
                        }`
                      : ''}
                  </TrueImpactText>
                </div>
                {wouldOrphan ? (
                  <span
                    className="orphan-warning"
                    title="Existing program answers will remain in the database but stop being copied to new reports."
                  >
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    Orphans {q.programAnswersCount}
                  </span>
                ) : (
                  <span />
                )}
                <TrueImpactIconButton
                  icon={faPen}
                  label="Edit"
                  onClick={() => onEditQuestion(q)}
                />
              </Row>
            );
          })}
        </Body>
      </TrueImpactCollapse>
    </Card>
  );
};

export default SectionAccordion;
